import React, { useState } from 'react';
import { SectionWrapper, CTAButton } from './ui';
import { ChevronDown, ShieldCheck, Truck, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  q: 'How does the LEVANCE™ Ultrasonic Tick Repellent actually work?',
  a: "LEVANCE™ emits a precisely calibrated ultrasonic frequency that is imperceptible to humans and pets but highly disruptive to ticks and other parasitic pests. The ultrasonic waves interfere with the tick's sensory organs, creating an invisible barrier that repels them before they can latch on. Simply turn on the device and enjoy tick-free outdoor time — no sprays, no chemicals, no mess."
}, {
  q: 'Is LEVANCE™ safe for my children and pets?',
  a: "Absolutely. LEVANCE™ uses ultrasonic technology that operates at a frequency completely outside the hearing range of humans, dogs, and cats. There are zero chemicals, zero toxins, and zero DEET involved. You can clip it on your child's backpack or your dog's harness with total peace of mind."
}, {
  q: 'How long does the battery last?',
  a: "LEVANCE™ features a long-lasting rechargeable battery that provides up to 120 hours of continuous protection on a single charge. Recharging takes approximately 1–2 hours via the included USB-C cable. A low-battery indicator light lets you know when it's time to recharge."
}, {
  q: "What's the effective range of the ultrasonic repellent?",
  a: 'LEVANCE™ provides a protective radius of approximately 10 feet (3 meters) in all directions from the device. This creates a personal tick-free zone around you, your children, or your pets. For larger areas like campsites, many families use two or more devices to extend the coverage zone.'
}, {
  q: 'How does LEVANCE™ compare to chemical tick sprays and collars?',
  a: "Chemical sprays contain DEET, permethrin, or other pesticides that can cause irritation — especially in children. Tick collars use similar chemicals that rub off on furniture and bedding. LEVANCE™ uses zero chemicals. It repels ticks through sound waves, not poisons. It's reusable (no monthly purchases) and works immediately."
}, {
  q: 'Is the device weather-resistant? Can I use it in the rain?',
  a: "Yes. LEVANCE™ is built with durable ABS housing that's splash and rain-resistant — fine for a misty morning walk, sudden showers, or damp forest trails. We don't recommend fully submerging it in water, but normal outdoor conditions are no problem."
}, {
  q: 'What is your 60-Day Money-Back Guarantee?',
  a: "We stand behind LEVANCE™ 100%. If you're not completely satisfied with your purchase for any reason, simply contact our support team within 60 days of receiving your order. We'll issue a full refund — no questions asked, no hoops to jump through."
}, {
  q: 'How quickly will I receive my order?',
  a: "Orders are processed within 1–2 business days and shipped with tracking provided via email. You'll receive a tracking number as soon as your order ships."
}, {
  q: 'Does it work on other pests besides ticks?',
  a: 'While LEVANCE™ is specifically tuned and optimized for tick repulsion, many customers report it also helps deter fleas and other small parasitic insects. However, our primary engineering focus and testing has been on tick prevention.'
}];
const AccordionItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-sage/50 last:border-0" data-id="element-24">
      <button className="w-full py-5 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-25">
        <span className="font-bold text-primary pr-4" data-id="element-26">{q}</span>
        <ChevronDown className={`w-5 h-5 text-accent shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-27" />
      </button>
      <AnimatePresence data-id="element-28">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-29">
            <div className="pb-5 text-[15px] leading-relaxed text-foreground/80" data-id="element-30">
              {a}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const FAQ = () => {
  return <SectionWrapper className="py-16 bg-white border-y border-sage/50" data-id="element-31">
      <h2 className="text-3xl font-serif text-primary font-bold mb-2 text-center" data-id="element-32">
        Frequently Asked Questions
      </h2>
      <p className="text-center text-foreground/70 mb-10" data-id="element-33">
        Everything you need to know about LEVANCE™ Ultrasonic Tick Repellent
      </p>

      <div className="max-w-3xl mx-auto bg-background border border-sage rounded-sm p-2 md:p-6" data-id="element-34">
        {faqs.map((faq, i) => <AccordionItem key={i} q={faq.q} a={faq.a} data-id="element-35" />)}
      </div>
    </SectionWrapper>;
};
export const FinalCTA = () => {
  return <SectionWrapper className="py-16 bg-primary text-white text-center" data-id="element-36">
      <div className="inline-flex items-center text-accent font-bold tracking-wider text-sm mb-6" data-id="element-37">
        ⚡ Limited Stock — Tick Season Is Here
      </div>

      <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6 text-background" data-id="element-38">
        Don't Wait for a Tick Bite to Take Action
      </h2>

      <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto" data-id="element-39">
        Protect your family and pets with chemical-free, ultrasonic tick
        defense. One device. Zero toxins. A full 60 days to try it risk-free.
      </p>

      <div className="text-2xl font-bold mb-8" data-id="element-40">
        $39.90{' '}
        <span className="text-white/50 line-through font-normal text-xl ml-2 mr-4" data-id="element-41">
          $98.00
        </span>
        <span className="text-accent text-lg" data-id="element-42">· BUY ONE GET ONE FREE</span>
      </div>

      <CTAButton className="w-full sm:w-auto text-lg py-5 px-12 mb-8" data-id="element-43">
        Get LEVANCE™ Now
      </CTAButton>

      <div className="flex flex-wrap justify-center gap-6 text-sm font-medium text-white/70" data-id="element-44">
        <span className="flex items-center gap-2" data-id="element-45">
          <ShieldCheck className="w-4 h-4" data-id="element-46" /> 60-Day Guarantee
        </span>
        <span className="flex items-center gap-2" data-id="element-47">
          <Truck className="w-4 h-4" data-id="element-48" /> Free Fast Shipping
        </span>
        <span className="flex items-center gap-2" data-id="element-49">
          <Lock className="w-4 h-4" data-id="element-50" /> Secure Checkout
        </span>
      </div>
    </SectionWrapper>;
};
export const SiteFooter = () => {
  return <footer className="bg-background py-10 border-t border-sage" data-id="element-51">
      <div className="max-w-5xl mx-auto px-5 text-center md:text-left flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-52">
        <div data-id="element-53">
          <div className="font-serif font-bold text-2xl text-primary tracking-tight mb-2" data-id="element-54">
            LEVANCE™
          </div>
          <p className="text-xs text-foreground/60" data-id="element-55">
            © {new Date().getFullYear()} LEVANCE. All rights reserved.
          </p>
        </div>

        <div className="flex gap-6 text-sm font-medium text-foreground/70" data-id="element-56">
          <a href="#" className="hover:text-primary transition-colors" data-id="element-57">
            Privacy Policy
          </a>
          <a href="#" className="hover:text-primary transition-colors" data-id="element-58">
            Terms of Service
          </a>
          <a href="#" className="hover:text-primary transition-colors" data-id="element-59">
            Contact
          </a>
        </div>
      </div>
    </footer>;
};